import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { getGradient } from 'webapp/utils/color';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import type { BoardSelectListItem } from 'webapp/components/boards/popup-select-list';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import Report from 'webapp/models-custom/report';
import { themeClassNames } from 'webapp/models-custom/theme';

export interface Args {
	image?: string;
	fallbackImage?: string;
	color?: string;
	theme?: string | number;
	count?: number;
	model?: BoardSelectListItem;
	emblemTypeName?: string;
	isLazyLoaded?: boolean;
	lazyLoadScrollContainer?: string;
	isVisibleOnScreen?: boolean;
	showLock?: boolean;
	forceBorder?: boolean;
	onImageClick?: () => void;
}

export default class BoardImagePreviewComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	get mediumImage(): string {
		let image = '';
		const originalImage = this.args.image;
		if (originalImage) {
			image = Board.snapshotResize(originalImage, 'm');
		}
		return image;
	}

	get largeImage(): string {
		let image = '';
		const originalImage = this.args.image;
		if (originalImage) {
			image = Board.snapshotResize(originalImage, 'l');
		}
		return image;
	}

	get srcImage(): string {
		return this.args.isLazyLoaded ? '' : this.mediumImage;
	}

	get dataSrcImage(): string {
		return this.args.isLazyLoaded ? this.mediumImage : '';
	}

	get dataSrcBigImage(): string {
		return this.args.isLazyLoaded ? this.largeImage : '';
	}

	get color(): string {
		return this.args.color || '#212222'; // 'Grey' from board-color-config
	}

	@cached
	get theme(): string {
		const theme = this.args.theme || 1;
		if (typeof theme === 'string') {
			return theme;
		} else {
			return this.mmBoardsService.getBgTheme(this.color, theme);
		}
	}

	get themeClass(): string {
		return themeClassNames(this.theme);
	}

	get showBorder(): boolean {
		return (this.theme === 'light' && !this.args.showLock) || !!this.args.forceBorder;
	}

	get gradientStyle() {
		if (this.args.image) {
			return '';
		}

		const colorsConfig = Array.from(this.constantsService.boardColors || []),
			gradient = getGradient(this.color, colorsConfig);

		return htmlSafe(gradient);
	}

	get fallbackImageStyle() {
		const fallbackImage = this.args.fallbackImage;
		let style = '';
		if (fallbackImage) {
			style = `background-image:url('${fallbackImage}')!important;opacity:0.8;`;
		}
		return htmlSafe(style);
	}

	get isImageClickable(): boolean {
		return !!this.args.onImageClick;
	}

	get isFavorite(): boolean {
		const model = this.args.model;
		if (model instanceof Board || model instanceof BoardLoop || model instanceof Report) {
			return model.favorite;
		}

		return false;
	}

	get isLoop(): boolean {
		return this.args.model instanceof BoardLoop;
	}

	@action
	onImageChange(node: HTMLElement): void {
		if (this.args.isLazyLoaded) {
			schedule('afterRender', () => {
				const imgNode = node.querySelector('img');
				// already lazyloaded, replace src directly
				if (imgNode) {
					imgNode.setAttribute('src', this.dataSrcImage);
				}
			});
		}
	}

	@action
	setupLazyLoad(node: HTMLElement): void {
		if (this.args.isLazyLoaded) {
			const imgNode = node.querySelector('img');
			if (imgNode) {
				const opts: { scrollContainer?: string } = {};
				if (this.args.lazyLoadScrollContainer) {
					opts.scrollContainer = this.args.lazyLoadScrollContainer;
				}
				$(imgNode).lazyLoadXT(opts);
			}
		}
	}

	@action
	onImageClick(): void {
		if (this.args.onImageClick) {
			this.args.onImageClick();
		}
	}
	<template>
		<div
			role="button"
			class="board-image-preview preview gradient
				{{this.themeClass}}
				{{if this.showBorder 'border'}}
				{{if this.isImageClickable 'clickable'}}"
			style={{this.gradientStyle}}
			{{didInsert this.setupLazyLoad}}
			{{didUpdate this.setupLazyLoad @isVisibleOnScreen}}
			{{didUpdate this.onImageChange @image}}
			{{on "click" this.onImageClick}}
		>
			{{#if @image}}
				<img
					class="img"
					src={{this.srcImage}}
					data-src={{this.dataSrcImage}}
					data-src-big={{this.dataSrcBigImage}}
					alt
				/>
			{{else}}
				<div class="empty" style={{this.fallbackImageStyle}}></div>
			{{/if}}

			{{#if @showLock}}
				<div class="blur">
					<CommonUiIconComponent @name="Lock" @size="lg" />
				</div>
			{{/if}}

			{{#if @count}}
				<span class="count">{{@count}}</span>
			{{/if}}

			{{#if @emblemTypeName}}
				<div class="emblem emblem--{{@emblemTypeName}}"></div>
			{{/if}}

			{{#if this.isFavorite}}
				<span class="favorite"><CommonUiIconComponent @name="StarFill" @size="sm" /></span>
			{{/if}}
			{{#if this.isLoop}}
				<span class="loop {{if this.isFavorite 'loop--right'}}">
					<CommonUiIconComponent @name="Loop" @size="md" />
				</span>
			{{/if}}
		</div>
	</template>
}
